import { checkShelfItemListing, linkShelfItemToListing } from '../../util/api';
import * as log from '../../util/log';

const LINK_RETRY_DELAYS_MS = [0, 500, 1500];

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const errorWithCode = (code, message, extra = {}) =>
  Object.assign(new Error(message), { code, ...extra });

/**
 * The My Shelf record a listing was created from. Stored in the listing's own privateData so it
 * survives page reloads between creating the draft and publishing it.
 *
 * @param {Object} listing own listing entity
 * @returns {string|null}
 */
export const sourceShelfItemIdOf = listing =>
  listing?.attributes?.privateData?.sourceShelfItemId || null;

/**
 * Refuse to publish a second marketplace listing for a shelf book that already has an active one.
 * A shelf book that no longer exists has nothing to protect, so publishing continues.
 *
 * @param {string} collectionItemId
 * @param {string} listingId the draft being published
 */
export const assertShelfItemNotListed = async (collectionItemId, listingId) => {
  let result;
  try {
    result = await checkShelfItemListing(collectionItemId, listingId);
  } catch (error) {
    if (error?.code === 'collection_item_not_found') return;
    if (error?.code === 'listing_sold') {
      throw errorWithCode('listing_sold', error.error || 'This book has already sold.');
    }
    throw errorWithCode(
      'shelf_check_failed',
      'We couldn’t confirm this book isn’t already listed. Please try again.'
    );
  }
  if (result?.status === 'existing') {
    throw errorWithCode(
      'shelf_item_already_listed',
      'This book already has a marketplace listing. Manage it from My Shelf instead.',
      { listing: result.listing }
    );
  }
};

/**
 * Persist the shelf → listing link after publishing and verify the server stored it.
 * Transient failures are retried; definite rejections (4xx) are not. Never throws: the listing is
 * already live, and My Shelf repairs the link from the listing's privateData on its next load.
 *
 * @returns {Promise<boolean>} whether the link is confirmed
 */
export const linkPublishedListingToShelf = async (
  collectionItemId,
  listingId,
  { delays = LINK_RETRY_DELAYS_MS } = {}
) => {
  let lastError = null;
  for (const delay of delays) {
    if (delay > 0) await wait(delay);
    try {
      const result = await linkShelfItemToListing(collectionItemId, listingId);
      if (result?.item?.source_listing_id === listingId) return true;
      lastError = new Error('Shelf link was not confirmed.');
    } catch (error) {
      lastError = error;
      if (error?.code && error.code !== 'source_link_unavailable') break;
    }
  }
  log.error(lastError, 'shelf-listing-link-failed', { collectionItemId, listingId });
  return false;
};
